<template>
  <div class="listing">
    <loader class="imageLoader" :loading="loading" />
    <header class="header">
      <div class="header__Search">
        <form @submit.prevent="fetch({ search_query: filters.search_query, only_root: false })" class="search">
          <input
            class="search__Text"
            type="search"
            :placeholder="$store.getters.getStringTranslation('enterTagNameEnter')"
            v-model="filters.search_query"
          />
          <button class="search__Button">
            <icon name="search"></icon>
          </button>
        </form>
      </div>
    </header>
    <section class="page">
      <header class="pageHeader">
        <div class="pageHeader__Inner">
          <h1 class="pageHeader__Title">{{ $store.getters.getStringTranslation('tags') }}</h1>

          <ul class="pageHeader__Meta">
            <li>
              <router-link class="button" :to="{ name: 'Add tag' }">{{
                $store.getters.getStringTranslation('addTag')
              }}</router-link>
            </li>
            <li>
              <router-link class="button" :to="{ name: 'Merge tags' }">{{
                $store.getters.getStringTranslation('mergeTags')
              }}</router-link>
            </li>
            <li>
              <button class="button" @click="downloadCsv">
                {{ $store.getters.getStringTranslation('downloadCSV') }}
              </button>
            </li>
          </ul>
        </div>
      </header>

      <transition name="fade">
        <router-view :propLanguage="lang" :types="types" :user="user" v-if="loading === false"></router-view>
      </transition>

      <div class="tab">
        <div class="tab__MenuWrapper">
          <nav class="tab__Menu">
            <ul class="tab__MenuList--Small">
              <li v-for="item in filters.status" :key="item.id">
                <a
                  :class="{ 'is-Active': searchParams.status === item.toLowerCase() }"
                  @click="filter(item, 'status')"
                  >{{ $store.getters.getStringTranslation(item) }}</a
                >
              </li>
            </ul>

            <ul class="tab__MenuList--Right">
              <li v-for="item in filters.order_by" :key="item.id">
                <a
                  :class="{ 'is-Active': searchParams.order_by === item.toLowerCase() }"
                  @click="filter(item, 'order_by')"
                  >{{ $store.getters.getStringTranslation(item.toLowerCase()) }}</a
                >
              </li>
              <li v-for="item in filters.order_dir" :key="item.id">
                <a
                  :class="{ 'is-Active': searchParams.order_dir === item.toLowerCase() }"
                  @click="filter(item, 'order_dir')"
                  >{{ $store.getters.getStringTranslation(item.toLowerCase()) }}
                </a>
              </li>
            </ul>
          </nav>

          <nav class="tab__Menu">
            <ul class="tab__MenuList--Dropdown">
              <select class="formSelect--Listing" v-model="curLang" @change="filter(curLang, 'lang')">
                <option :value="item.value" v-for="item in filters.lang" :key="item.value">
                  {{ item.label }}
                </option>
              </select>
            </ul>

            <ul class="tab__MenuList--Right">
              <li>
                <a @click="filter([], 'types')" :class="{ 'is-Active': searchParams.types.length === 0 }">{{
                  $store.getters.getStringTranslation('all')
                }}</a>
              </li>
              <li v-for="item in filters.types" :key="item.id">
                <a :class="{ 'is-Active': searchParams.types.includes(item) }" @click="filter([item], 'types')">{{
                  $store.getters.getStringTranslation(item.toLowerCase(item))
                }}</a>
              </li>
            </ul>
          </nav>
        </div>

        <div class="tab__Wrapper">
          <div class="tab__Item">
            <div class="tab__Content">
              <table class="postsTable" :class="{ 'has-Selected': selected.length > 0 }" v-if="resources.length > 0">
                <thead>
                  <tr>
                    <th class="has-Checkbox">
                      <input type="checkbox" v-model="selectAll" id="selectAll" />
                    </th>
                    <th>{{ $store.getters.getStringTranslation('title') }}</th>
                    <th>{{ $store.getters.getStringTranslation('type') }}</th>
                  </tr>
                </thead>
                <tbody v-for="(item, index) in resources" :key="item.id">
                  <tag
                    :class="{ 'has-Children': item.subTags.length > 0 }"
                    v-bind="{ item, index, selected, types, lang }"
                    @input="select"
                    :key="item.id"
                  ></tag>

                  <template v-for="(item, index) in item.subTags">
                    <tag
                      class="is-Child"
                      v-bind="{ item, index, selected, types, lang }"
                      @input="select"
                      :key="item.id"
                    ></tag>
                  </template>
                </tbody>
              </table>

              <div class="error" v-else-if="errors.length > 0">
                <p>Something went wrong! Please let our administrators know of the problems below.</p>

                <pre>{{ errors }}</pre>
              </div>

              <div class="error" v-else-if="resources.length === 0 && loading === false">
                <p>Sorry, no content matched your criteria.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <transition name="slideUp">
        <actions
          v-show="this.selected.length > 0"
          v-bind="{ selectAll, selected }"
          :items="resources"
          type="tag"
        ></actions>
      </transition>

      <pagination v-bind="{ searchParams, count }" dispatch="searchTags" />
    </section>
  </div>
</template>

<script>
import { languages } from '@/router/globals.js'
import EventBus from '@/bus.js'

import Tag from '@/components/listing/Tag.vue'
import Actions from '@/components/listing/Actions.vue'
import Loader from '@/components/partials/Loader.vue'
import Pagination from '@/components/partials/Pagination.vue'

import SelectAll from '@/components/mixins/SelectAll.js'
import Capitalize from '@/components/mixins/Capitalize.js'

import HTTP from '@/http-common.js'

export default {
  name: 'Tags',
  components: {
    Tag,
    Actions,
    Loader,
    Pagination,
  },
  props: ['title', 'buttons', 'editor', 'cells', 'types', 'user'],
  mixins: [SelectAll, Capitalize],
  data() {
    return {
      curLang: 'en',
      filters: {
        status: ['published', 'disabled'],
        lang: languages,
        types: this.types,
        search_query: '',
        order_by: ['Updated', 'Inserted', 'Alphabetical'],
        order_dir: ['DESC', 'ASC'],
      },
      errors: '',
    }
  },
  mounted() {
    let defaultParams = {
      status: 'published',
      lang: this.lang,
      types: [],
      sub_tags: true,
      only_root: true,
    }
    if (this.$route.query.search !== undefined) {
      defaultParams = JSON.parse(this.$route.query.search)
    }
    this.fetch(defaultParams)

    EventBus.$on('clearSelected', () => {
      this.selected = []
    })
  },
  computed: {
    resources() {
      const tags = this.$store.state.tags.search.tags
      if (tags) return tags
      return []
    },
    count() {
      const count = this.$store.state.tags.search.counts
      if (count && Object.keys(count).length > 0) return Object.values(count).reduce((a, b) => a + b)

      return 0
    },
    searchParams() {
      const params = this.$store.state.tags.searchParams
      if (params) return params
      return 0
    },
    lang() {
      const query = this.$route.query
      if (query.search && JSON.parse(query.search).lang) return JSON.parse(query.search).lang
      if (query.lang) return query.lang
      return this.$store.state.ui.lang
    },
    loading() {
      return this.$store.state.tags.loading
    },
  },
  watch: {
    searchParams: {
      deep: true,
      handler() {
        this.pushRoute()
      },
    },
  },
  methods: {
    async fetch(obj) {
      try {
        await this.$store.dispatch('searchTags', obj)
      } catch (e) {
        console.log(e)
      }
    },
    downloadCsv() {
      HTTP.get('download_all_tags_csv').then((response) => {
        let fileContent = 'data:text/csv;charset=utf-8,'
        fileContent += response.data
        const encodedUri = encodeURI(fileContent)
        window.open(encodedUri)
      })
    },
    pushRoute() {
      // Push search params to URL query
      this.selected = []
      this.$router.push({ query: { search: JSON.stringify(this.searchParams) } })
    },
    filter(value, type) {
      if (typeof value === 'string') value = value.toLowerCase()
      this.fetch({ [type]: value, offset: 0 })
    },
  },
}
</script>
